import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { usePostHog } from "@posthog/react";

import { MadeBy } from "../components/made-by";
import { readStoredTheme, themeTokens } from "../theme";
import {
  AudienceRequestError,
  type AudienceQuestion,
  askQuestion,
  fetchQuestions,
  normaliseRoomCode,
  readVoted,
  rememberVote,
  voteForQuestion,
} from "../qa/audience-client";

// The audience page: someone standing in a room with a phone, who has never
// heard of Copresent and will be here for twenty minutes.
//
// Everything follows from that. No account, no install, no onboarding. The ask
// box is the first thing under the thumb, the list below it is the reason to
// stay, and the only chrome is one line saying where they are. They arrive by
// scanning a QR or typing six characters off a screen, so the page has to work
// on a bad conference network and survive being backgrounded mid-talk.

const POLL_INTERVAL_MS = 5000;
const MAX_LENGTH = 280;

/** Names are optional and default to off — anonymity is what gets questions asked. */
const NAME_KEY = "copresentAudienceName";

const Page = styled.div<{ $dark: boolean }>`
  ${(p) => themeTokens(p.$dark)}

  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 14px 40px;
  box-sizing: border-box;
  background: var(--bg-page);
  color: var(--text-primary);
  font-family: inherit;
`;

const Shell = styled.div`
  width: 100%;
  max-width: 560px;
`;

const Header = styled.header`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 2px 14px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: var(--text-headline);
`;

const RoomCode = styled.span`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--text-fade);
  font-variant-numeric: tabular-nums;
`;

const Card = styled.section`
  background: var(--bg-card);
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 1px 2px var(--shadow-near);
`;

const Composer = styled(Card)`
  position: sticky;
  top: 8px;
  z-index: 2;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 74px;
  resize: vertical;
  border: 1px solid var(--border-chip);
  border-radius: 10px;
  padding: 11px 12px;
  /* 16px minimum, or iOS Safari zooms the whole page on focus — which on a
     phone in a dark room is genuinely disorienting. */
  font-size: 16px;
  font-family: inherit;
  line-height: 1.45;
  background: var(--bg-fallback);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--link);
  }
`;

const ComposerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const NameInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 1px solid var(--border-chip);
  border-radius: 10px;
  padding: 9px 11px;
  font-size: 16px;
  font-family: inherit;
  background: var(--bg-fallback);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--link);
  }
`;

const AskButton = styled.button`
  flex-shrink: 0;
  border: none;
  border-radius: 10px;
  padding: 11px 18px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  background: var(--link);
  color: #fff;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Counter = styled.span<{ $over: boolean }>`
  font-size: 12px;
  color: ${(p) => (p.$over ? "#ef4444" : "var(--text-fade)")};
  font-variant-numeric: tabular-nums;
`;

const Notice = styled.p<{ $tone?: "error" }>`
  margin: 10px 2px 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${(p) => (p.$tone === "error" ? "#ef4444" : "var(--text-muted)")};
`;

const ListHeading = styled.h2`
  margin: 26px 2px 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-fade);
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.li<{ $answered: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: var(--bg-card);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  padding: 12px;
  opacity: ${(p) => (p.$answered ? 0.55 : 1)};
`;

const VoteButton = styled.button<{ $voted: boolean }>`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  min-width: 46px;
  padding: 6px 4px;
  border-radius: 9px;
  border: 1px solid ${(p) => (p.$voted ? "var(--link)" : "var(--border-chip)")};
  background: ${(p) => (p.$voted ? "var(--link)" : "transparent")};
  color: ${(p) => (p.$voted ? "#fff" : "var(--text-secondary)")};
  font-family: inherit;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const VoteCount = styled.span`
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;

const Body = styled.div`
  min-width: 0;
  flex: 1;
`;

const QuestionText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: var(--text-primary);
  overflow-wrap: anywhere;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-fade);
`;

const Tag = styled.span`
  border: 1px solid var(--border-soft);
  border-radius: 999px;
  padding: 1px 8px;
`;

const Empty = styled(Card)`
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
  line-height: 1.6;
`;

const Centered = styled.div`
  margin: 64px auto 0;
  max-width: 380px;
  text-align: center;
  color: var(--text-muted);
  font-size: 15px;
  line-height: 1.6;
`;

const Footer = styled(MadeBy)`
  margin-top: 32px;
  text-align: center;
  font-size: 12px;
  color: var(--text-fade);
`;

const ArrowUp = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 19V5M12 5l-6 6M12 5l6 6"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type Phase = "loading" | "open" | "unavailable";

const Audience = () => {
  // Canonicalised once, here, so nothing downstream ever sees the raw spelling.
  // The service accepts several spellings of the same code, and anything keyed
  // off the raw param — the voted set, the code on screen — would otherwise
  // differ depending on how the visitor arrived.
  const { code: typedCode = "" } = useParams();
  const code = normaliseRoomCode(typedCode);
  const posthog = usePostHog();

  const [dark] = useState(readStoredTheme() === "dark");
  const [phase, setPhase] = useState<Phase>("loading");
  const [questions, setQuestions] = useState<AudienceQuestion[]>([]);
  const [voted, setVoted] = useState<Set<string>>(() => readVoted(code));

  const [text, setText] = useState("");
  const [name, setName] = useState(() => {
    try {
      return window.localStorage.getItem(NAME_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<{ message: string; tone?: "error" } | null>(null);
  const [stale, setStale] = useState(false);

  const etag = useRef<string | null>(null);

  // Votes cast but not yet reflected in a fetched list. The server coalesces
  // vote writes for a few seconds, so without this the tap appears to do nothing
  // until the next flush — which reads as a broken button and invites a re-tap.
  const [optimistic, setOptimistic] = useState<Record<string, number>>({});

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const snapshot = await fetchQuestions(code, etag.current, signal);
        etag.current = snapshot.etag;
        if (snapshot.questions) {
          setQuestions(snapshot.questions);
          // The fetched list now includes anything we were holding optimistically.
          setOptimistic({});
        }
        setStale(false);
        setPhase("open");
      } catch (error) {
        if (signal?.aborted) return;
        if (error instanceof AudienceRequestError) {
          if (error.detail.kind === "unavailable") {
            setPhase("unavailable");
            return;
          }
          // A network blip must not blank the list someone is reading. Keep what
          // we have and say so quietly.
          setStale(true);
          return;
        }
        setStale(true);
      }
    },
    [code],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Poll, but only while the page is actually being looked at. A talk runs for
  // an hour and people put their phones in their pockets; polling a backgrounded
  // tab for that long costs them battery and us requests, and buys nobody
  // anything. Resuming refetches immediately so returning feels instant.
  useEffect(() => {
    if (phase === "unavailable") return undefined;

    let timer: number | undefined;

    const tick = () => {
      if (document.visibilityState === "visible") void load();
    };

    const start = () => {
      window.clearInterval(timer);
      timer = window.setInterval(tick, POLL_INTERVAL_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void load();
        start();
      } else {
        window.clearInterval(timer);
      }
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load, phase]);

  const trimmed = text.trim();
  const tooLong = trimmed.length > MAX_LENGTH;
  const canSend = trimmed.length > 0 && !tooLong && !sending;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSend) return;

    setSending(true);
    setNotice(null);

    try {
      const author = name.trim() ? name.trim() : null;
      await askQuestion(code, trimmed, author);

      setText("");
      try {
        if (author) window.localStorage.setItem(NAME_KEY, author);
      } catch {
        /* storage disabled; the name simply is not remembered */
      }

      posthog?.capture("qa_question_asked", { named: author !== null });
      setNotice({ message: "Sent. Everyone can see it now." });

      // The server drops its cache on a write, so this returns the new question
      // rather than a stale list.
      etag.current = null;
      await load();
    } catch (error) {
      if (error instanceof AudienceRequestError) {
        if (error.detail.kind === "rate_limited") {
          setNotice({
            message: `That's a lot of questions at once. Try again in ${error.detail.retryAfterSeconds}s.`,
            tone: "error",
          });
        } else if (error.detail.kind === "unavailable") {
          setPhase("unavailable");
        } else {
          setNotice({
            message: "Couldn't send that. Check your connection and try again.",
            tone: "error",
          });
        }
      }
    } finally {
      setSending(false);
    }
  };

  const vote = async (question: AudienceQuestion) => {
    if (voted.has(question.id)) return;

    // Optimistic, and recorded locally straight away: the server never tells us
    // who voted for what (that would let anyone probe it), so this device's
    // memory is the only place that knows.
    setVoted((previous) => new Set(Array.from(previous)).add(question.id));
    setOptimistic((previous) => ({ ...previous, [question.id]: 1 }));
    rememberVote(code, question.id);
    posthog?.capture("qa_question_voted");

    try {
      await voteForQuestion(code, question.id);
    } catch (error) {
      if (error instanceof AudienceRequestError && error.detail.kind === "unavailable") {
        setPhase("unavailable");
        return;
      }
      // Roll back only the count. The local "you voted" mark stays, because a
      // failed vote that silently un-highlights looks like the tap was ignored;
      // the next successful poll reconciles the number either way.
      setOptimistic((previous) => {
        const next = { ...previous };
        delete next[question.id];
        return next;
      });
    }
  };

  const shown = useMemo(() => {
    const withOptimistic = questions.map((question) => ({
      ...question,
      votes: question.votes + (optimistic[question.id] ?? 0),
    }));

    return withOptimistic.sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      if (a.votes !== b.votes) return b.votes - a.votes;
      return a.askedAt.localeCompare(b.askedAt);
    });
  }, [questions, optimistic]);

  if (phase === "loading") {
    return (
      <Page $dark={dark}>
        <Centered>Loading…</Centered>
      </Page>
    );
  }

  if (phase === "unavailable") {
    return (
      <Page $dark={dark}>
        <Shell>
          <Centered>
            <p style={{ margin: 0, fontWeight: 600, color: "var(--text-headline)" }}>
              This Q&amp;A isn't open
            </p>
            {/* One message for every cause, because the server deliberately
                returns one status for all of them — an unknown code, an expired
                one, or a host who closed the room. Guessing which on the
                client would only be wrong out loud. */}
            <p style={{ marginTop: 8 }}>
              The code may be wrong, or the presenter may have closed questions.
              Check the screen for the current code.
            </p>
          </Centered>
          <Footer />
        </Shell>
      </Page>
    );
  }

  return (
    <Page $dark={dark}>
      <Shell>
        <Header>
          <Title>Ask a question</Title>
          {/* Already canonical — an uppercase of the raw param would still show
              the O the visitor typed where the real code has a zero. */}
          <RoomCode aria-label={`Room code ${code}`}>{code}</RoomCode>
        </Header>

        <Composer as="form" onSubmit={submit}>
          <TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What would you like to ask?"
            aria-label="Your question"
            maxLength={MAX_LENGTH * 2}
          />
          <ComposerRow>
            <NameInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              aria-label="Your name, optional"
              maxLength={40}
            />
            <AskButton type="submit" disabled={!canSend}>
              {sending ? "Sending…" : "Ask"}
            </AskButton>
          </ComposerRow>
          <ComposerRow style={{ justifyContent: "space-between", marginTop: 8 }}>
            <Counter $over={tooLong}>
              {trimmed.length}/{MAX_LENGTH}
            </Counter>
            {!name.trim() && (
              <Counter $over={false}>Posting anonymously</Counter>
            )}
          </ComposerRow>
          {notice && <Notice $tone={notice.tone}>{notice.message}</Notice>}
        </Composer>

        <ListHeading>
          {shown.length === 0
            ? "No questions yet"
            : `${shown.length} question${shown.length === 1 ? "" : "s"}`}
          {stale && " · reconnecting…"}
        </ListHeading>

        {shown.length === 0 ? (
          <Empty>Be the first to ask. Questions with the most votes rise to the top.</Empty>
        ) : (
          <List>
            {shown.map((question) => {
              const hasVoted = voted.has(question.id);
              return (
                <Row key={question.id} $answered={question.answered}>
                  <VoteButton
                    type="button"
                    $voted={hasVoted}
                    onClick={() => void vote(question)}
                    disabled={hasVoted}
                    aria-label={
                      hasVoted
                        ? `You voted. ${question.votes} vote${question.votes === 1 ? "" : "s"}`
                        : `Vote for this question. ${question.votes} vote${
                            question.votes === 1 ? "" : "s"
                          }`
                    }
                  >
                    <ArrowUp />
                    <VoteCount>{question.votes}</VoteCount>
                  </VoteButton>
                  <Body>
                    <QuestionText>{question.text}</QuestionText>
                    <Meta>
                      <span>{question.authorName ?? "Anonymous"}</span>
                      {question.slideIndex != null && <Tag>Slide {question.slideIndex}</Tag>}
                      {question.answered && <Tag>Answered</Tag>}
                    </Meta>
                  </Body>
                </Row>
              );
            })}
          </List>
        )}

        <Footer />
      </Shell>
    </Page>
  );
};

export default Audience;
